import React, { useState, useRef, useEffect } from 'react';
import { useModalLayer } from '../../lib/modalStack';
import { PHONE_COUNTRIES, detectCountryByDial, stripDial, formatE164 } from '../../lib/phoneCountries';
import type { CountryPhoneMeta } from '../../types';

interface Props {
  value: string;          // Full E.164 number or raw number
  countryCode?: string;   // ISO alpha-2 country code for dial prefix detection
  onChange: (e164: string, country: CountryPhoneMeta, nationalDigits: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  label?: string;
  className?: string;
  /** Error message to display */
  error?: string;
}

const stripNonDigits = (v: string) => v.replace(/\D/g, '');

export default function InternationalPhoneInput({
  value,
  countryCode,
  onChange,
  onBlur,
  disabled,
  required,
  placeholder,
  label,
  className = '',
  error,
}: Props) {
  // Detect initial country from existing value or profile countryCode
  const initialCountry = React.useMemo(() => {
    if (value) {
      const detected = detectCountryByDial(value);
      if (detected) return detected;
    }
    if (countryCode) {
      const found = PHONE_COUNTRIES.find(c => c.code === countryCode);
      if (found) return found;
    }
    return PHONE_COUNTRIES[0]; // default US
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [selected, setSelected] = useState<CountryPhoneMeta>(initialCountry);
  const [localDigits, setLocalDigits] = useState(() => {
    if (value) {
      const detected = detectCountryByDial(value);
      if (detected) return stripDial(detected.dial, value);
    }
    return stripNonDigits(value || '');
  });
  const [open, setOpen] = useState(false);
  // SYS-11: layer assigned by the modal stack while open (the z scale
  // has no generated named utilities in Tailwind v4 — overlays register
  // with the stack instead; see index.css).
  const layerZ = useModalLayer(open);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter countries by search
  const filtered = search.trim()
    ? PHONE_COUNTRIES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.dial.includes(search) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : PHONE_COUNTRIES;

  // Notify parent when country or digits change
  const notifyChange = (country: CountryPhoneMeta, digits: string) => {
    const e164 = formatE164(country, digits);
    onChange(e164, country, stripNonDigits(digits));
  };

  const handleCountrySelect = (country: CountryPhoneMeta) => {
    setSelected(country);
    setOpen(false);
    setSearch('');
    notifyChange(country, localDigits);
    inputRef.current?.focus();
  };

  const handleDigitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, spaces, dashes, parens
    const raw = e.target.value;
    const cleaned = raw.replace(/[^0-9\s\-()]/g, '');
    setLocalDigits(cleaned);
    // Strip non-digits for E.164
    const digitsOnly = stripNonDigits(cleaned);
    notifyChange(selected, digitsOnly);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isValid = localDigits ? selected.pattern.test(stripNonDigits(localDigits)) : true;

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-[11px] font-bold text-slate-500 uppercase">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
      )}
      <div className="flex gap-1.5">
        {/* Country selector */}
        <div className="relative shrink-0" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            disabled={disabled}
            className="h-full flex items-center gap-1.5 px-2.5 py-2.5 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all cursor-pointer disabled:cursor-not-allowed disabled:bg-slate-50 text-xs min-w-[72px]"
          >
            <span className="text-base leading-none">{selected.flag}</span>
            <span className="text-xs font-semibold text-slate-600">{selected.dial}</span>
            <svg className={`h-3 w-3 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>

          {open && (
            <div className="absolute left-0 bottom-full mb-1 w-64 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-hidden" style={{ zIndex: layerZ }}>
              <div className="p-2 border-b border-slate-100">
                <input
                  autoFocus
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search countries..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400"
                />
              </div>
              <div className="overflow-y-auto max-h-48">
                {filtered.map(c => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleCountrySelect(c)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left bg-brand-active transition-all cursor-pointer ${selected.code === c.code ? 'bg-blue-50 font-bold' : ''}`}
                  >
                    <span className="text-base">{c.flag}</span>
                    <span className="text-slate-500 font-mono text-[10px] w-8">{c.dial}</span>
                    <span className="text-slate-700">{c.name}</span>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p className="px-3 py-3 text-xs text-slate-400 text-center">No countries found</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone number input */}
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="tel"
            value={localDigits}
            onChange={handleDigitsChange}
            onBlur={onBlur}
            disabled={disabled}
            required={required}
            placeholder={placeholder || selected.placeholder}
            className={`w-full border rounded-lg px-3 py-2.5 text-xs outline-none transition-all disabled:bg-slate-50 disabled:cursor-not-allowed ${
              error ? 'border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-100' :
              !isValid && localDigits ? 'border-amber-300 focus:border-amber-400' :
              'border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-brand-primary'
            }`}
          />
          {!isValid && localDigits && !error && (
            <p className="text-[10px] text-amber-600 mt-0.5">Invalid number for {selected.name}</p>
          )}
        </div>
      </div>
    </div>
  );
}
